<template>
  <div class="calendar-tooltip">
    <!-- Navigation mois/année -->
    <div class="p-2 border-b border-gray-200 bg-white rounded-t-lg">
      <div class="flex justify-between items-center">
        <button
          @click="previousMonth"
          class="p-1 rounded hover:bg-gray-100 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 class="text-xl font-semibold text-gray-800">
          {{ monthNames[currentMonth] }} {{ currentYear }}
        </h2>
        <button
          @click="nextMonth"
          class="p-1 rounded hover:bg-gray-100 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Jours de la semaine -->
    <div class="p-1 border-b border-gray-200 bg-white">
      <div class="grid grid-cols-7 gap-2 text-center">
        <div v-for="day in weekDays" :key="day" class="text-lg font-semibold text-gray-600 py-1">
          {{ day }}
        </div>
      </div>
    </div>

    <!-- Grille du calendrier -->
    <div class="p-2 bg-white rounded-b-lg">
      <div class="grid grid-cols-7 gap-2">
        <!-- Cases vides avant le premier jour -->
        <div
          v-for="empty in firstDayOffset"
          :key="'empty-' + empty"
          class="aspect-square"
        ></div>

        <!-- Jours du mois -->
        <button
          v-for="day in daysInMonth"
          :key="day"
          @click="selectDate(day)"
          class="aspect-square rounded border transition-all hover:bg-gray-50 hover:border-primary-300 flex flex-col items-center justify-center relative"
          :class="{
            'bg-primary text-white border-primary': isToday(day),
            'text-gray-900 border-gray-200': !isToday(day) && !isSelected(day),
            'ring-2 ring-primary bg-primary/10 border-primary': isSelected(day) && !isToday(day),
            'text-gray-400': !isInCurrentMonth(day),
            'font-bold': isToday(day)
          }"
        >
          <span class="text-lg">{{ day }}</span>
          <!-- Indicateur si cette date a des matches -->
          <div
            v-if="hasMatchesOnDate(day)"
            class="absolute bottom-0.5 w-1 h-1 bg-green-500 rounded-full"
          ></div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  matchesDates: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['select'])

// État du calendrier
const currentMonth = ref(new Date().getMonth())
const currentYear = ref(new Date().getFullYear())
const selectedDate = ref(new Date())

const monthNames = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
]

const weekDays = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

// Calculer le nombre de jours dans le mois
const daysInMonth = computed(() => {
  return new Date(currentYear.value, currentMonth.value + 1, 0).getDate()
})

// Calculer le décalage du premier jour (Lundi = 0, Dimanche = 6)
const firstDayOffset = computed(() => {
  const firstDay = new Date(currentYear.value, currentMonth.value, 1).getDay()
  return (firstDay === 0 ? 6 : firstDay - 1)
})

// Vérifier si une date est aujourd'hui
const isToday = (day) => {
  const today = new Date()
  return day === today.getDate() &&
         currentMonth.value === today.getMonth() &&
         currentYear.value === today.getFullYear()
}

// Vérifier si une date est sélectionnée
const isSelected = (day) => {
  return day === selectedDate.value.getDate() &&
         currentMonth.value === selectedDate.value.getMonth() &&
         currentYear.value === selectedDate.value.getFullYear()
}

// Vérifier si une date est dans le mois actuel
const isInCurrentMonth = (day) => {
  return true // Toutes les dates affichées sont dans le mois courant
}

// Vérifier si une date a des matches
const hasMatchesOnDate = (day) => {
  const date = new Date(currentYear.value, currentMonth.value, day)

  // Calculer la différence en jours depuis la date de base (10 oct 2024)
  const baseDate = new Date(2024, 9, 10)
  const diffTime = date.getTime() - baseDate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  let dateId
  if (diffDays < 0) {
    dateId = `past-${Math.abs(diffDays)}`
  } else if (diffDays === 0) {
    dateId = 'today'
  } else {
    dateId = `future-${diffDays}`
  }

  return props.matchesDates.includes(dateId)
}

// Navigation
const previousMonth = () => {
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
}

const nextMonth = () => {
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
}

const goToToday = () => {
  const today = new Date()
  currentMonth.value = today.getMonth()
  currentYear.value = today.getFullYear()
  selectedDate.value = today
  emit('select', today)
}

const selectDate = (day) => {
  const date = new Date(currentYear.value, currentMonth.value, day)
  selectedDate.value = date
  emit('select', date)
}

</script>

<style scoped>
.grid-cols-7 {
  grid-template-columns: repeat(7, minmax(0, 1fr));
}
</style>